/**
 * Session Replay records request and response bodies for our backend (`networkDetailAllowUrls`, index.tsx), and
 * replays are stored exactly as recorded: Sentry's server-side data scrubbing does not run on them. So credential
 * VALUES are replaced here before an event is added, and nothing else is: the URL, method, status, timing, every
 * other field and the shape of the body all stay, because the replay is what a production bug is debugged from.
 * An empty credential is kept as it is, so "the server sent no token" stays visible.
 */

/** Keys whose value is a credential: `token`, `accessToken`, `jwtToken`, `password`, `newPassword`, `clientSecret`, `otp`… */
const CREDENTIAL_KEY = /(password|passwd|token|secret|api_?key|otp)$/i;
/**
 * The same keys inside JSON text that Sentry kept as a string (a body over its size limit, cut off mid-value). A
 * cut-off value ends the string, so `$` stands in for its closing quote.
 */
const CREDENTIAL_IN_TEXT =
  /("[^"\\]*?(?:password|passwd|token|secret|api_?key|otp)"\s*:\s*)(?:"(?:[^"\\]|\\.)*(?:"|$)|-?\d+(?:\.\d+)?)/gi;
const REDACTED = "[Filtered]";

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const isCredentialValue = (value: unknown): boolean =>
  (typeof value === "string" && value !== "") || typeof value === "number";

/** `body` with every credential value replaced; booleans such as `hasPassword` and empty values are kept. */
export function redactCredentials(body: unknown): unknown {
  if (typeof body === "string") return body.replace(CREDENTIAL_IN_TEXT, `$1"${REDACTED}"`);
  if (Array.isArray(body)) return body.map(redactCredentials);
  if (isRecord(body)) {
    return Object.fromEntries(
      Object.entries(body).map(([key, value]) => [
        key,
        CREDENTIAL_KEY.test(key) && isCredentialValue(value) ? REDACTED : redactCredentials(value),
      ]),
    );
  }
  return body;
}

/** Sentry Replay's `beforeAddRecordingEvent`: scrubs the bodies of recorded XHR/fetch calls, passes every other event through. */
export function scrubRecordingEvent<E>(event: E): E {
  if (!isRecord(event) || !isRecord(event.data) || event.data.tag !== "performanceSpan") return event;
  const span = event.data.payload;
  if (!isRecord(span) || (span.op !== "resource.xhr" && span.op !== "resource.fetch") || !isRecord(span.data)) {
    return event;
  }
  for (const side of ["request", "response"]) {
    const part = span.data[side];
    if (isRecord(part) && part.body !== undefined) part.body = redactCredentials(part.body);
  }
  return event;
}
